import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useCredits } from '../../hooks/useCredits';
import { useTranslation } from 'react-i18next';
import searchService from '../../services/searchService';
import axiosClient from '../../api/axiosClient';
import ProfileDropdown from './ProfileDropdown';
import NotificationCenter from './NotificationCenter';
import LanguageSwitcher from '../LanguageSwitcher';
import { buildShopScopedPath, resolveUserShopId } from '../../utils/adminShopContext';
import { getRoleDisplayNameLocalized } from '../../utils/userDisplay';

const AdminTopbar = ({ onMenuToggle, onSearchStateChange, hasImpersonationBanner, shellConfig = null }) => {
  const { isDark, toggleTheme } = useTheme();
  const { user, hasPermission } = useAdminAuth();
  const { t } = useTranslation('admin');
  const effectiveUser = shellConfig?.user || user;
  const effectiveHasPermission = shellConfig?.hasPermission || hasPermission;
  const effectiveShopId = shellConfig?.shopId || resolveUserShopId(effectiveUser);
  const creditsPath = shellConfig?.creditsPath || '/admin/credits';
  const showCreditsBadge = !shellConfig?.hideCredits && effectiveUser?.role !== 'superadmin';
  const { credits, loading: creditsLoading, refetch: refetchCredits } = useCredits();
  const [brandingLogo, setBrandingLogo] = useState({ url: null, version: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchDropdownRect, setSearchDropdownRect] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const creditsRemaining = credits?.credits_remaining ?? 0;
  const shouldPulseCreditsDot = !creditsLoading && creditsRemaining > 0;
  const getSearchStatusLabel = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return '';

    const keyMap = {
      active: 'admin.status_active',
      pending: 'admin.status_pending',
      cancelled: 'admin.status_cancelled',
      canceled: 'admin.status_cancelled',
      confirmed: 'admin.status_confirmed',
      completed: 'admin.status_completed',
      closed: 'admin.status_closed',
      unread: 'admin.notifications_unread',
      read: 'admin.notifications_read'
    };

    return t(keyMap[normalized] || normalized, { defaultValue: status });
  };

  const updateDropdownRect = () => {
    const el = searchInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const vvOffsetLeft = vv?.offsetLeft || 0;
    const vvOffsetTop = vv?.offsetTop || 0;
    const viewportW = vv?.width || window.innerWidth;
    const padding = 8;
    const availableWidth = Math.max(0, viewportW - padding * 2);
    const minWidth = Math.min(560, availableWidth);
    const width = Math.min(Math.max(rect.width, minWidth), availableWidth);

    // Clamp within viewport (helps iPad Safari where layout/visual viewport differs)
    const desiredLeft = rect.left + vvOffsetLeft;
    const centeredLeft = desiredLeft + rect.width / 2 - width / 2;
    const left = Math.max(padding, Math.min(centeredLeft, viewportW - width - padding));

    setSearchDropdownRect({
      top: rect.bottom + 8 + vvOffsetTop,
      left,
      width,
    });
  };

  const dropdownStyle = useMemo(() => {
    if (!searchDropdownRect) return undefined;
    return {
      position: 'fixed',
      top: `${searchDropdownRect.top}px`,
      left: `${searchDropdownRect.left}px`,
      width: `${searchDropdownRect.width}px`,
      zIndex: 1000, // above panels/drawers
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    };
  }, [searchDropdownRect]);

  // Refetch credits when route changes (useful after AI generation)
  useEffect(() => {
    refetchCredits();
  }, [location.pathname, refetchCredits]);

  // Also refetch immediately when any AI tool signals a credit was consumed.
  // Pages dispatch window.dispatchEvent(new Event('credits:changed')) after generation.
  useEffect(() => {
    const handler = () => refetchCredits();
    window.addEventListener('credits:changed', handler);
    return () => window.removeEventListener('credits:changed', handler);
  }, [refetchCredits]);

  // Load current branding logo and subscribe to live updates (Settings → Branding)
  useEffect(() => {
    let mounted = true;

    const loadBrandingLogo = async () => {
      if (shellConfig?.disableBrandingFetch || !effectiveShopId) {
        if (mounted) {
          setBrandingLogo({ url: null, version: null });
        }
        return;
      }

      try {
        const resp = await axiosClient.get(
          buildShopScopedPath('/admin/settings/branding/logo', effectiveShopId)
        );
        if (!mounted) return;
        setBrandingLogo({
          url: resp.data?.customLogoUrl || null,
          version: resp.data?.version ?? null,
        });
      } catch (e) {
        // Non-blocking: fall back to default logo silently.
      }
    };

    const onBrandingUpdate = (event) => {
      const detail = event?.detail || {};
      setBrandingLogo({
        url: detail.url || null,
        version: detail.version ?? Date.now(),
      });
    };

    loadBrandingLogo();
    window.addEventListener('branding:logo-updated', onBrandingUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('branding:logo-updated', onBrandingUpdate);
    };
  }, [effectiveShopId, shellConfig?.disableBrandingFetch]);

  const hasCustomLogo = !!brandingLogo.url;
  const logoSrc = hasCustomLogo
    ? `${brandingLogo.url}${brandingLogo.version ? `?v=${brandingLogo.version}` : ''}`
    : '/images/logos/Hairchitech_Logo1.png';

  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length >= 2) {
      setIsSearching(true);
      setShowSearchResults(true);
      
      // Debounce search requests
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = shellConfig?.globalSearch
            ? await shellConfig.globalSearch(query)
            : await searchService.globalSearch(query, {
                includeBookings: effectiveHasPermission('bookings'),
                includeClients: effectiveHasPermission('clients'),
                includeBarbers: effectiveHasPermission('barbers'),
                includeServices: effectiveHasPermission('services'),
                includeInventory: effectiveHasPermission('inventory'),
                includeAiTools: effectiveHasPermission('ai-analysis') || effectiveHasPermission('clients'),
              });
          setSearchResults(results);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
    }
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      if (shellConfig?.handleSearchSubmit) {
        shellConfig.handleSearchSubmit(searchQuery, navigate);
      } else if (effectiveHasPermission('bookings')) {
        navigate(`/admin/bookings?q=${encodeURIComponent(searchQuery)}`);
      } else if (effectiveHasPermission('clients')) {
        navigate(`/admin/clients?q=${encodeURIComponent(searchQuery)}`);
      } else if (effectiveHasPermission('barbers')) {
        navigate(`/admin/barbers?q=${encodeURIComponent(searchQuery)}`);
      }
      setShowSearchResults(false);
    }
  };

  // Handle search result click
  const handleResultClick = (result) => {
    navigate(result.url);
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside search input OR search results dropdown
      const isClickInsideSearch = searchRef.current && searchRef.current.contains(event.target);
      const isClickInsideDropdown = event.target.closest('[data-search-dropdown]');
      
      if (!isClickInsideSearch && !isClickInsideDropdown) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keep dropdown positioned correctly (scroll/resize) while open
  useEffect(() => {
    if (!showSearchResults) return;

    updateDropdownRect();

    const onScroll = () => updateDropdownRect();
    const onResize = () => updateDropdownRect();

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // iPad/iOS Safari: dropdown can drift when the visual viewport moves (keyboard, pinch zoom)
    window.visualViewport?.addEventListener('scroll', onScroll);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onScroll);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [showSearchResults]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Notify parent component when search state changes
  useEffect(() => {
    if (onSearchStateChange) {
      onSearchStateChange(showSearchResults);
    }
  }, [showSearchResults, onSearchStateChange]);

  return (
    <header 
      className={`h-24 w-full transition-all duration-500 ease-out admin-topbar ${isDark ? 'theme-dark' : 'theme-light'}`}
      onClick={() => {
        // Remove stopPropagation to allow button clicks to work properly
      }}
    >
      
        {/* Content Container - NEVER scrolls (header must not become a scroll container) */}
        <div className="relative z-10 flex items-center gap-1 min-[1025px]:gap-2 px-2 min-[1025px]:px-3 h-24 max-w-full overflow-hidden">
        
        {/* Left Section: Aligned with Sidebar Content (desktop only) */}
        <div className="hidden min-[1025px]:flex items-center gap-4 w-64 px-6 shrink-0">
          {/* Logo */}
          <div className="flex items-center">
            <div className="relative flex items-center" style={{ width: 240, height: 84 }}>
              {hasCustomLogo ? (
                <img
                  src={logoSrc}
                  alt={t('admin.custom_logo_alt')}
                  className={`shrink-0 ${isDark ? 'admin-logo-dark' : 'admin-logo-light'}`}
                  style={{
                    maxWidth: 240,
                    maxHeight: 80,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    display: 'block',
                  }}
                />
              ) : (
                <img
                  src={logoSrc}
                  alt={t('admin.default_logo_alt')}
                  width={100}
                  height={100}
                  className={`shrink-0 ${isDark ? 'admin-logo-dark' : 'admin-logo-light'}`}
                  style={{
                    maxWidth: 100,
                    maxHeight: 80,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    display: 'block',
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Mobile + Tablet: logo only (matches mobile view) */}
        <div className="flex items-center gap-1 min-[1025px]:hidden shrink-0">
          {/* Mobile Logo */}
          <div
            className="relative flex items-center shrink-0"
            style={{ width: 110, height: 84, maxWidth: '110px', marginLeft: '8px' }}
          >
            {hasCustomLogo ? (
              <img
                src={logoSrc}
                alt={t('admin.custom_logo_alt')}
                className={`shrink-0 ${isDark ? 'admin-logo-dark' : 'admin-logo-light'}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: 72,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={logoSrc}
                alt={t('admin.default_logo_alt')}
                className={`shrink-0 ${isDark ? 'admin-logo-dark' : 'admin-logo-light'}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: 72,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  display: 'block',
                }}
              />
            )}
          </div>
        </div>

        {/* Center/Main Section: Global Search - Desktop only */}
        <div className="hidden min-[1025px]:block flex-1 max-w-3xl relative z-50 min-w-0 px-2 min-[1025px]:px-0" ref={searchRef}>
          <form onSubmit={handleSearch} className="relative w-full">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={shellConfig?.searchPlaceholder || t('admin.search_placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => {
                if (searchQuery.length >= 2) {
                  setShowSearchResults(true);
                }
              }}
              className={`w-full pl-10 pr-4 py-3 admin-search clickable-item ${isDark ? 'theme-dark' : 'theme-light'} min-w-0`}
              style={{
                background: isDark ? '#2a2f36' : '#f9f9f9',
                border: isDark ? '1px solid #383e45' : '1px solid #dddddd',
                color: isDark ? '#e7ebef' : 'var(--text)',
                borderRadius: '12px',
                boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,.06)'
              }}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isSearching ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-transparent border-t-current text-c9a038"></div>
              ) : (
                <svg 
                  className="h-5 w-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: isDark ? '#98a0a8' : '#5a5a5a'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                  />
                </svg>
              )}
            </div>
          </form>

          {/* Search Results Dropdown - Theme-aware */}
          {showSearchResults && typeof document !== 'undefined' && createPortal(
            <div 
              data-search-dropdown
              className={`rounded-xl shadow-2xl max-h-96 overflow-y-auto ${
                isDark 
                  ? 'bg-gray-800/95 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}
              style={{
                ...dropdownStyle,
                maxWidth: 'calc(100vw - 16px)',
              }}
            >
              {searchResults.length > 0 ? (
                <div className="py-2">
                  {searchResults.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}-${index}`}
                      onClick={() => handleResultClick(result)}
                      className={`w-full px-4 py-3 text-left transition-all duration-200 rounded-lg mx-2 ${
                        isDark 
                          ? 'text-white hover:bg-white/10' 
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg backdrop-blur-sm ${
                          result.type === 'booking' 
                            ? isDark 
                              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                              : 'bg-blue-100 text-blue-700 border border-blue-200'
                            : result.type === 'client' 
                              ? isDark 
                                ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                                : 'bg-green-100 text-green-700 border border-green-200'
                              : result.type === 'barber' 
                                ? isDark 
                                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                                  : 'bg-purple-100 text-purple-700 border border-purple-200'
                                : isDark 
                                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' 
                                  : 'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                          {result.type === 'booking' && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          {result.type === 'client' && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          )}
                          {result.type === 'barber' && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {result.type === 'service' && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {result.title}
                          </div>
                          <div className={`text-sm truncate ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                            {result.subtitle}
                          </div>
                        </div>
                        <div className={`text-xs px-2 py-1 rounded-full backdrop-blur-sm ${
                          result.status === 'active' 
                            ? isDark 
                              ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                              : 'bg-green-100 text-green-700 border border-green-300'
                            : result.status === 'pending' 
                              ? isDark 
                                ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                                : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                              : result.status === 'cancelled' 
                                ? isDark 
                                  ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                                  : 'bg-red-100 text-red-700 border border-red-300'
                                : isDark 
                                  ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' 
                                  : 'bg-gray-100 text-gray-700 border border-gray-300'
                        }`}>
                          {getSearchStatusLabel(result.status)}
                        </div>
                      </div>
                    </button>
                  ))}
                  <div className={`px-4 py-2 border-t ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
                    <button
                      onClick={handleSearch}
                      className={`w-full text-left text-sm font-medium transition-colors ${
                        isDark 
                          ? 'text-[#c9a038] hover:text-[#c9a038]/80' 
                          : 'text-[#7d6633] hover:text-[#7d6633]/80'
                      }`}
                    >
                      {t('admin.search_view_all')} "{searchQuery}"
                    </button>
                  </div>
                </div>
              ) : searchQuery.length >= 2 && !isSearching ? (
                <div className={`px-4 py-3 text-center ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {t('admin.search_no_results_for')} "{searchQuery}"
                </div>
              ) : null}
            </div>,
            document.body
          )}
        </div>

        {/* Right Section: Actions – keep on one line, never wrap */}
        <div className="ml-auto flex items-center gap-1.5 min-[1025px]:gap-2 shrink-0 min-w-0 px-1 min-[1025px]:px-6">
          {/* Credits Badge — hidden on very small phones to prevent overflow */}
          {showCreditsBadge ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(creditsPath);
              }}
              className={`admin-credits-button clickable-item hidden min-[400px]:flex ${isDark ? 'theme-dark' : 'theme-light'}`}
              data-pulse={shouldPulseCreditsDot ? 'true' : 'false'}
              aria-label={creditsLoading ? t('admin.loading_credits') : t('admin.credits_remaining_aria', { count: creditsRemaining })}
              title={creditsLoading ? t('admin.loading_credits') : t('admin.credits_manage_title')}
            >
              {creditsLoading ? (
                <div className="admin-credits-inner">
                  <span className="admin-credits-dot" aria-hidden="true" />
                  <span className="admin-credits-value">
                    <span className="animate-spin inline-block rounded-full h-4 w-4 border-2 border-transparent border-t-current align-[-2px] mr-1" aria-hidden="true" />
                    ...
                  </span>
                </div>
              ) : (
                <div className="admin-credits-inner">
                  <span className="admin-credits-dot" aria-hidden="true" />
                  <span className="admin-credits-value">{creditsRemaining}</span>
                </div>
              )}
            </button>
          ) : null}
          
          {/* Notifications (visible on all screens) */}
          <div className="flex-shrink-0">
            <NotificationCenter shellConfig={shellConfig} />
          </div>

          {/* Theme toggle (visible on all screens) */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleTheme();
            }}
            className={`p-2 admin-icon-button clickable-item flex-shrink-0 ${isDark ? 'theme-dark' : 'theme-light'}`}
            style={{
              color: isDark ? '#e7ebef' : '#5a5a5a'
            }}
            aria-label={t('admin.theme')}
            title={isDark ? t('admin.switch_to_light_mode') : t('admin.switch_to_dark_mode')}
          >
            {isDark ? (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" 
                />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" 
                />
              </svg>
            )}
          </button>
          
          {/* Role badge - Desktop only for cleaner tablet/mobile UI */}
          <span 
            className="hidden min-[1025px]:inline-flex px-3 py-1 text-xs rounded-full font-medium clickable-item flex-shrink-0"
            style={{
              background: isDark ? 'rgba(125,102,51,.18)' : 'var(--gold-300)',
              color: isDark ? '#f0d9a6' : 'var(--gold-600)',
              border: isDark ? '1px solid rgba(125,102,51,.3)' : '1px solid var(--gold-400)'
            }}
          >
            {getRoleDisplayNameLocalized(effectiveUser?.role, t)}
          </span>
          
          {/* Language switcher — visible on all devices */}
          <div className="flex-shrink-0">
            <LanguageSwitcher />
          </div>

          {/* Profile dropdown (desktop only; moved into sidebar on mobile/tablet) */}
          <div className="hidden min-[1025px]:block flex-shrink-0">
            <ProfileDropdown shellConfig={shellConfig} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
